import { useVirtualizer } from '@tanstack/react-virtual'
import React from 'react'
import styled from 'styled-components'

import { getMeasureElement } from './utils'

export type ElementVirtualizerType = ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>

interface ElementVirtualizerProps {
  'data-e2e'?: string
  children: (virtualizer: ElementVirtualizerType) => React.ReactNode
  dimensions: { height: number; hasOverflowY: boolean; hasOverflowX: boolean }
  rowHeight: number
  rowsLength: number
  scrollElement: HTMLDivElement | null
  setHorizontalScrollingAreaRef: (element: HTMLDivElement | null) => void
}

export const HorizontalScrollingArea = styled.div<{
  $height: number
  $hasOverflowY: boolean
  $hasOverflowX: boolean
}>`
  padding-right: ${props =>
    !props.$hasOverflowY || props.$hasOverflowX ? props.theme.spacings.medium : 0};
  margin-left: ${props => props.theme.spacings.medium};
  max-width: calc(100% - ${props => props.theme.spacings.medium});
  width: calc(100% - ${props => props.theme.spacings.medium});
  height: ${props => props.$height}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin-bottom: -${props => props.theme.spacings.medium};
`

export const ElementVirtualizer = ({
  'data-e2e': dataE2e,
  children,
  dimensions,
  rowHeight,
  rowsLength,
  scrollElement,
  setHorizontalScrollingAreaRef,
}: ElementVirtualizerProps) => {
  const rowVirtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: rowsLength,
    estimateSize: () => rowHeight,
    getScrollElement: () => scrollElement,
    measureElement: getMeasureElement(),
    overscan: 10,
  })

  return (
    <HorizontalScrollingArea
      $hasOverflowX={dimensions.hasOverflowX}
      $hasOverflowY={dimensions.hasOverflowY}
      $height={dimensions.height}
      data-e2e={`${dataE2e}--scrolling-area`}
      ref={setHorizontalScrollingAreaRef}
    >
      {children(rowVirtualizer)}
    </HorizontalScrollingArea>
  )
}
